import { useQueryClient } from '@tanstack/react-query';
import { BookOpen, CheckCircle2, FileText, Pencil, PencilLine, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  getArtigosControllerListarQueryKey,
  useArtigosControllerExcluir,
  useArtigosControllerListar,
} from '@/api/generated/assessoria/assessoria';
import type { ArtigoResumoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { ArtigoFormDialogo } from '@/features/assessoria/components/ArtigoFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';

const STATUS = {
  RASCUNHO: {
    rotulo: 'Rascunho',
    icone: PencilLine,
    classe: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200',
  },
  PUBLICADO: {
    rotulo: 'Publicado',
    icone: CheckCircle2,
    classe:
      'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200',
  },
} as const;

/**
 * Lado do advisor — "Meus artigos". Os publicados aparecem para o médico no
 * rodapé de toda discussão que este advisor atende.
 */
export function MeusArtigosPage() {
  const queryClient = useQueryClient();

  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<string | undefined>();
  const [paraExcluir, setParaExcluir] = useState<ArtigoResumoResponse | undefined>();

  const { data, isPending, isError, refetch } = useArtigosControllerListar();
  const excluir = useArtigosControllerExcluir();

  function abrirNovo() {
    setEmEdicao(undefined);
    setFormAberto(true);
  }

  function confirmarExclusao() {
    if (!paraExcluir) return;

    excluir.mutate(
      { id: paraExcluir.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getArtigosControllerListarQueryKey() });
          toast.success('Artigo removido');
          setParaExcluir(undefined);
        },
        onError: (erro) =>
          toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível remover'),
      },
    );
  }

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-brand" />
            Meus artigos
          </h2>
          <p className="text-muted-foreground mt-1">
            Os artigos publicados aparecem para o médico no rodapé das discussões que você atende.
          </p>
        </div>

        <Button onClick={abrirNovo} className="gap-2 bg-brand text-brand-foreground hover:bg-brand/90">
          <Plus className="w-4 h-4" />
          Novo artigo
        </Button>
      </div>

      {isPending ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : data.itens.length === 0 ? (
        <EstadoVazio
          icone={BookOpen}
          titulo="Nenhum artigo ainda"
          descricao="Escreva um artigo ou importe o texto de um PDF."
          acao={
            <Button onClick={abrirNovo} className="gap-2 bg-brand text-brand-foreground">
              <Plus className="w-4 h-4" />
              Novo artigo
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {data.itens.map((artigo) => {
            const status = STATUS[artigo.status as keyof typeof STATUS];
            const IconeDeStatus = status.icone;

            return (
              <Card key={artigo.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-brand" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{artigo.titulo}</h3>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{artigo.trecho}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Atualizado em {new Date(artigo.atualizadoEm).toLocaleDateString('pt-BR')}
                          {artigo.temPdf && ' · com PDF original'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge className={`${status.classe} border gap-1 font-medium`}>
                        <IconeDeStatus className="w-3 h-3" />
                        {status.rotulo}
                      </Badge>

                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Editar"
                          onClick={() => {
                            setEmEdicao(artigo.id);
                            setFormAberto(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          title="Excluir"
                          onClick={() => setParaExcluir(artigo)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {formAberto && (
        <ArtigoFormDialogo
          aberto={formAberto}
          aoFechar={() => {
            setFormAberto(false);
            setEmEdicao(undefined);
          }}
          {...(emEdicao ? { artigoId: emEdicao } : {})}
        />
      )}

      <AlertDialog open={!!paraExcluir} onOpenChange={(v) => !v && setParaExcluir(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir artigo</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{paraExcluir?.titulo}&quot; será removido, junto com o PDF original. Esta ação não
              pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarExclusao}
              disabled={excluir.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
